import { hasAvailability } from '@utils/helpers';
import { mapDiscountToDisplayObject } from '@utils/mapDiscountToDisplayObject';

import { useLoadingState } from '@common/hooks';
import { useGetSpaTreatments, useGetSpaTreatmentsPage } from '@features/spa/hooks';

import { CategoryFilterValue } from '@features/spa/hooks/useSpaEntityFilter';
import { UNCATEGORIZED_VALUE } from '@features/spa/utils/buildCategoryOptions';
import { SpaEntityType } from 'shared-types/Spa';
import { SpaListItem } from 'voyager';

type UseSpaTreatmentsListParams = {
  propertyId: number;
  language: string;
  categoryFilterId: number | null;
  categoryFilterValue: CategoryFilterValue;
  date: string;
  guests?: number;
};

const mapToListItem = (entity: SpaEntityType, fallbackImageUrl?: string): SpaListItem => ({
  id: String(entity.id),
  name: entity.name,
  imageUrl: entity.image?.url || fallbackImageUrl,
  price:
    entity.price?.amount != null
      ? { amount: entity.price.amount, currencyCode: entity.price.currencyCode }
      : undefined,
  discount: mapDiscountToDisplayObject(entity.discount),
  promotionName: entity.discount?.name,
  duration: entity.displayDuration > 0 ? `${entity.displayDuration} min` : undefined,
  minGuests: entity.minimumGuests,
  maxGuests: entity.maximumGuests ?? entity.numberOfGuests,
  unavailable: !entity.availability,
});

export function useSpaTreatmentsList({
  propertyId,
  language,
  categoryFilterId,
  categoryFilterValue,
  date,
  guests,
}: UseSpaTreatmentsListParams) {
  const { spaTreatments = [], isFetching: isFetchingTreatments } = useGetSpaTreatments(
    propertyId,
    language,
    date,
    null,
    0,
    true
  );

  const hasLoadedState = useLoadingState(isFetchingTreatments, spaTreatments.length > 0);

  const { data: treatmentsPage } = useGetSpaTreatmentsPage(propertyId, language);
  const fallbackImageUrl = treatmentsPage?.heroBackgroundImage?.url;

  const sortedTreatments = spaTreatments.slice().sort((a, b) => a.priority - b.priority);

  const availableTreatments = sortedTreatments.filter((item) => hasAvailability(item, date));

  const unavailableTreatments = sortedTreatments.filter((item) => !hasAvailability(item, date));

  const allEntities = [...availableTreatments, ...unavailableTreatments];

  const categoryFiltered =
    categoryFilterValue === UNCATEGORIZED_VALUE
      ? allEntities.filter(({ categories }) => !categories || categories.length === 0)
      : categoryFilterId
      ? allEntities.filter(({ categories }) =>
          categories.find(
            (category: { id: string | number }) => String(category.id) === String(categoryFilterId)
          )
        )
      : allEntities;

  const filteredEntities = guests
    ? categoryFiltered.filter(
        (e) => (e.minimumGuests ?? 0) <= guests && (e.maximumGuests ?? Infinity) >= guests
      )
    : categoryFiltered;

  const listItems: SpaListItem[] = filteredEntities.map((entity) =>
    mapToListItem(entity, fallbackImageUrl)
  );

  const uncategorizedItems = spaTreatments.filter(
    (item) => !item.categories || item.categories.length === 0
  );

  return {
    listItems,
    uncategorizedItems,
    isFetching: isFetchingTreatments,
    ...hasLoadedState,
  };
}
